import React, { useEffect } from 'react';

import { useAuth } from 'auth';
import { useStorageService, STORAGE_KEYS } from 'services/storage';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faSignIn,
	faUser
} from '@fortawesome/pro-light-svg-icons';

import { ProjectsList } from './ProjectsList';
import AddButton from 'components/action/AddButton';

import Loader from 'components/Loading/Loader';
import ProjectModal, { defaultImpacts, defaultLikelihoods, defaultRiskCategories, defaultIndicatorCategories } from 'components/Modal/ProjectModal';
import { AI_GENERATION_ENABLED } from 'config/featureFlags';
// IA activée sur tous les envs (prod incluse) : on charge les modales en lazy
// partout (chunks séparés). La garde JSX en dessous utilise AI_GENERATION_ENABLED.
const AIProjectGenerationModal = React.lazy(() => import('components/Modal/AIProjectGenerationModal'));

const DisambiguationModal = React.lazy(() => import('components/Modal/DisambiguationModal'));

import {
	useConsumeCodeMutation
} from 'graph/dist/generated';
import { useMutation } from '@apollo/client';
import { CLAIM_ATTACHMENTS } from 'graph/documents';

import { useListProjectsQuery, useListSharedProjectsQuery } from 'graph/queries';
import { Button, Flex, Input, Layout, Segmented, Space, Tooltip, theme } from 'antd';

import './Projects.sass';
import { useProjectController } from 'hooks/useProjectController';
import WaitModal from 'components/Modal/WaitModal';
import Logo from 'components/Layout/Logo';
import { ThemeToggle } from 'components/Layout/ThemeToggle';

export const Projects = (props: any) => {
	const { isAuthenticated, login } = useAuth();
	const storage = useStorageService();
	const [editedItem, setEditedItem] = React.useState<any | null>(null);
	const { token } = theme.useToken();

	const [consumeCode] = useConsumeCodeMutation();
	const [claimAttachments] = useMutation(CLAIM_ATTACHMENTS);
	const { createProject, cloneProject, deleteProject } = useProjectController();

	const [searchTerm, setSearchTerm] = React.useState<any | null>(
		() => storage.getItem(STORAGE_KEYS.PROJECTS_SEARCH) || ''
	);
	// Filtre par type memorise en local (page portfolio) : conserve au retour
	const [projectTypeFilter, setProjectTypeFilter] = React.useState<any | null>(
		() => storage.getItem(STORAGE_KEYS.PROJECTS_TYPE_FILTER) || null
	);
	// Filtre propriété : all | owner | reader (projets partagés en lecture).
	const [ownershipFilter, setOwnershipFilter] = React.useState<any>(
		() => storage.getItem(STORAGE_KEYS.PROJECTS_OWNERSHIP_FILTER) || 'all'
	);

	// Pre-flight disambiguation modal: opens BEFORE createProject runs, so a
	// cancellation leaves no phantom project in DDB (Pierre 2026-06-05).
	// pendingPayload holds the raw form values + the AI-gen options so the
	// onClarified callback can run the actual createProject with the
	// (potentially enriched) descriptionLong.
	const [disambiguationState, setDisambiguationState] = React.useState<{
		visible: boolean;
		pendingPayload: {
			values: any;
			aiScenarios: any;
			aiContext: any;
			tempSessionId: any;
		} | null;
	}>({ visible: false, pendingPayload: null });

	const [aiGenerationState, setAiGenerationState] = React.useState<{
		visible: boolean;
		projectId: string;
		projectName: string;
		projectDescriptionLong: string;
		projectDescription: string;
		analysisLanguage: string;
		projectType: string;
		aiContext?: string;
		aiScenarios?: Array<{ label: string; content: string; horizonValue: number; horizonUnit: 'd' | 'w' | 'm' | 'y' }>;
	}>({
		visible: false,
		projectId: '',
		projectName: '',
		projectDescriptionLong: '',
		projectDescription: '',
		analysisLanguage: 'en',
		projectType: 'pro'
	});

	const { data, refetch, loading } = useListProjectsQuery();
	// Projets partagés AVEC moi (lecture seule). Fusionnés dans le portfolio.
	// listSharedProjects renvoie des rows membres ; on prend leur `project`.
	const { data: sharedData } = useListSharedProjectsQuery();
	const sharedProjects = (sharedData?.listSharedProjects || [])
		.map((m: any) => m?.project)
		.filter(Boolean);

	useEffect(() => {
		props.subscribeToProjects();

		return () => {
			props.unsubscribeFromProjects();
		};
	}, []);

	const { t } = useTranslation();
	let history = useHistory();

	const hasLessThanAuthorizedProjects =
		((data && data.listProjects && data.listProjects?.items?.length) || 0) <
		props.maxProjectsCount;

	const canCreateProjects =
		props.subscriptionsAreDisabled || hasLessThanAuthorizedProjects;

	useEffect(() => {
		if (props.setExpanded) props.setExpanded(false);

		if (storage.hasItem(STORAGE_KEYS.REDEEM_CODE)) {
			const code = storage.getItem(STORAGE_KEYS.REDEEM_CODE);
			consumeCodeAndUpdateSubscription(code);
		}
	}, [storage]);

	// Actually create the project + claim attachments + open AI-gen modal.
	// Used both by the legacy (no-disambiguation) path and by the post-
	// disambiguation continuation when the user has clarified the subject.
	const performCreateAndAiGen = async (values: any, opts: { aiGenerate: boolean; aiScenarios: any; aiContext: any; tempSessionId: any; }) => {
		const result = await createProject(values);

		// Claim staged attachments BEFORE starting AI analysis — otherwise
		// the analyze_situation lambda races the claim and reads 0
		// attachments. Sequential await guarantees the DDB rows have
		// projectId set when analysis kicks off.
		if (opts.tempSessionId && result) {
			try {
				await claimAttachments({ variables: { projectId: result, tempSessionId: opts.tempSessionId } });
			} catch (e) {
				console.warn('[Projects] claimAttachments failed', e);
			}
		}

		await refetch();
		props.deselect();
		props.resetBotThread();

		if (AI_GENERATION_ENABLED && opts.aiGenerate && result) {
			setAiGenerationState({
				visible: true,
				projectId: result,
				projectName: values.name || '',
				projectDescriptionLong: values.descriptionLong || '',
				projectDescription: values.description || '',
				analysisLanguage: t('messages.ai_project_generation.language_code'),
				projectType: values.type || 'pro',
				aiContext: opts.aiContext,
				aiScenarios: opts.aiScenarios
			});
		} else {
			history.push('/projects/' + result);
		}

		return result;
	};

	const handleCreateProject = async (values: any) => {
		const aiGenerate = values.aiGenerate;
		const aiScenarios = values.aiScenarios;
		const aiContext = values.aiContext;
		const tempSessionId = values._tempSessionId;
		delete values.aiGenerate;
		delete values.aiScenarios;
		delete values.aiContext;
		delete values._tempSessionId;

		if (!values.riskCategories) {
			values.riskCategories = defaultRiskCategories(t);
		}

		if (!values.indicatorCategories) {
			values.indicatorCategories = defaultIndicatorCategories(t);
		}

		if (!values.likelihoods) {
			values.likelihoods = defaultLikelihoods(t);
		}
		if (!values.impacts) {
			values.impacts = defaultImpacts(t);
		}

		// Pre-flight disambiguation BEFORE creating the project (Pierre
		// 2026-06-05): if the user cancels the clarification we don't want a
		// phantom project sitting in DynamoDB. The modal collects an
		// enriched description; once cleared, we run the actual createProject
		// with that enriched description and continue into AI gen.
		if (AI_GENERATION_ENABLED && aiGenerate) {
			setDisambiguationState({
				visible: true,
				pendingPayload: {
					values,
					aiScenarios,
					aiContext,
					tempSessionId
				}
			});
			return;
		}

		// Legacy path (no AI gen) — straight to create + redirect.
		return performCreateAndAiGen(values, { aiGenerate, aiScenarios, aiContext, tempSessionId });
	};

	const handleCloneProject = async (id: string, values) => {
		setTimeout(() => props.pushModal(<WaitModal></WaitModal>), 100);
		await cloneProject(id, values, async (newId) => {
			props.popModal();
			await refetch();
		});
	}

	const handleSelectProject = async (projectId: string) => {
		if (props.deselect) props.deselect();
		if (props.resetBotThread) props.resetBotThread();
		history.push('/projects/' + projectId);
	};

	const consumeCodeAndUpdateSubscription = async (code) => {
		await consumeCode({ variables: { id: code } });
		storage.removeItem(STORAGE_KEYS.REDEEM_CODE);
		await props.refreshSubscription();
	};

	// Fusion « mes projets » (createdBy) + « partagés avec moi » (reader/editor).
	// Dédup par id par sécurité (un projet ne devrait pas figurer dans les deux).
	const ownedProjects = data?.listProjects?.items || [];
	const seenIds = new Set(ownedProjects.map((p) => p.id));
	const allProjects = [...ownedProjects, ...sharedProjects.filter((p) => !seenIds.has(p.id))];

	const filteredProjects = allProjects.filter((project: any) => {

		// Filtre propriété : cohérent avec la visibilité du Segmented (masqué si aucun projet partagé).
		if (sharedProjects.length > 0) {
			const isShared = project?.myRole === 'reader';
			if (ownershipFilter === 'owner' && isShared) return false;
			if (ownershipFilter === 'reader' && !isShared) return false;
		}

		if (projectTypeFilter && projectTypeFilter !== "all" && projectTypeFilter !== project.type) return false;

		if (!searchTerm) return true;

		let terms = searchTerm ? searchTerm.split(' ') : [];
		return terms.every((term) => {
			return project.name.toLowerCase().includes(term.toLowerCase()) || project.reference?.toLowerCase().includes(term.toLowerCase())
		});
	});

	return (
		<Layout className="Projects">

			<Layout.Header>
				<Flex align="center" justify="center" style={{ height: '55px' }}>

					<Logo className="mr-2" primaryColor={token.colorSuccess} secondaryColor={token.colorTextBase} />

					<h2 className="mb-0">{t('pages.projects.title')}</h2>

					<div className="ml-2 mr-auto">
						{t('pages.projects.description')}
					</div>

					<Space >
						<div style={{ marginTop: '-3px' }}>
							<ThemeToggle />
						</div>

						{props.isFeatureEnabledProfile && (

							!isAuthenticated ? (
								<>
									<Tooltip title={t('pages.profile.title')}>
										<Button
											onClick={(e) => {
												e.preventDefault();
												login();
											}}
											shape="circle"
										>
											<FontAwesomeIcon icon={faSignIn} />
										</Button>
									</Tooltip>
								</>

							) : (
								<>
									<Tooltip title={t('pages.profile.title')}>
										<Button
											shape="circle"
											onClick={() => {
												history.push('/profile');
											}}
										>
											<FontAwesomeIcon icon={faUser} />
										</Button>
									</Tooltip>
								</>
							)
						)}
					</Space>
				</Flex>
			</Layout.Header>

			<Layout.Content className="pt-2 pb-2">
				{!props.subscriptionsAreDisabled &&
					(loading || props.subscriptionIsLoading) ? (

					<Loader></Loader>

				) : (
					<>
						<Flex align="center" justify="center" className="mb-4">
							<Input
								className="SearchInput"
								placeholder={t('pages.projects.search_projects')}
								value={searchTerm}
								onChange={(e) => {
									setSearchTerm(e.target.value);
									if (e.target.value) {
										storage.setItem(STORAGE_KEYS.PROJECTS_SEARCH, e.target.value);
									} else {
										storage.removeItem(STORAGE_KEYS.PROJECTS_SEARCH);
									}
								}}
							></Input>

							<Segmented
								className="Segmented ml-2 align-self-center"
								options={[
									{
										label: "All",
										value: "all"
									},
									{
										label: t("models.project.fields.types.stakeholders"),
										value: "stakeholders"
									},
									{
										label: t("models.project.fields.types.risks"),
										value: "risks"
									},
									{
										label: t("models.project.fields.types.pro"),
										value: "pro"
									},
								]}
								onChange={(value) => {
									setProjectTypeFilter(value);
									if (value) {
										storage.setItem(STORAGE_KEYS.PROJECTS_TYPE_FILTER, value);
									} else {
										storage.removeItem(STORAGE_KEYS.PROJECTS_TYPE_FILTER);
									}
								}}
							/>

							{sharedProjects.length > 0 && (
								<Segmented
									className="Segmented ml-2 align-self-center"
									value={ownershipFilter}
									options={[
										{ label: t('pages.projects.ownership.all', { defaultValue: 'Tous' }), value: 'all' },
										{ label: t('pages.projects.ownership.owner', { defaultValue: 'Propriétaire' }), value: 'owner' },
										{ label: t('pages.projects.ownership.reader', { defaultValue: 'Lecteur' }), value: 'reader' },
									]}
									onChange={(value) => {
										setOwnershipFilter(value);
										storage.setItem(STORAGE_KEYS.PROJECTS_OWNERSHIP_FILTER, value as string);
									}}
								/>
							)}
						</Flex>

						<ProjectsList
							pushModal={props.pushModal}
							popModal={props.popModal}
							onSelectProject={handleSelectProject}
							canClone={canCreateProjects}
							onClone={handleCloneProject}
							onDelete={async (id) => {
								setTimeout(() => props.pushModal(
									<WaitModal message={t("models.project.messages.delete_in_progress")} />
								), 100);
								try {
									await deleteProject(id);
									await refetch();
								} finally {
									props.popModal();
								}
							}}
							list={filteredProjects}
							refetch={refetch}
							authorizedProjectTypes={props.authorizedProjectTypes}

						></ProjectsList>
						{canCreateProjects ? (
							<div className="footer mr-auto ml-auto" style={{ width: "90%" }}>
								<AddButton
									onClick={() => {
										setEditedItem({});
									}}

								></AddButton>{' '}
							</div>
						) : (
							// Pas de CTA « abonnez-vous » si l'utilisateur a au moins un
							// projet partagé en lecture : il a une raison légitime d'être
							// là sans abonnement (Pierre 2026-07-21).
							!props.isSubscriber && sharedProjects.length === 0 && (
								<div
									className="subscribeNow"
									onClick={() => history.push('/profile')}
								>
									{t('pages.projects.cta.welcome')} <br />
									<u>{t('pages.projects.cta.subscribe')}</u>{' '}
									{t('pages.projects.cta.create_permission')}
								</div>
							)
						)}
					</>
				)
				}

			{
				editedItem !== null && (
					<ProjectModal
						onHide={() => {
							setEditedItem(null);
						}}
						onSave={async (values: Object) => {
							const projectId = await handleCreateProject(values);
							setEditedItem(null);
							return projectId;
						}}
						id={editedItem?.id}
						key={editedItem?.id || 'new'}
						values={{
							name: editedItem?.name,
							reference: editedItem?.reference,
							description: editedItem?.description,
							descriptionLong: editedItem?.descriptionLong,
							likelihoods: editedItem?.likelihoods,
							impacts: editedItem?.impacts,
							tags: editedItem?.tags,
							riskCategories: editedItem?.riskCategories,
							indicatorCategories: editedItem?.indicatorCategories,
							type: null
						}}
						authorizedTypes={props.authorizedProjectTypes}
					></ProjectModal>
				)
			}

			{AI_GENERATION_ENABLED && AIProjectGenerationModal && (
				<React.Suspense fallback={null}>
					<AIProjectGenerationModal
						visible={aiGenerationState.visible}
						projectId={aiGenerationState.projectId}
						projectName={aiGenerationState.projectName}
						projectDescriptionLong={aiGenerationState.projectDescriptionLong}
						projectDescription={aiGenerationState.projectDescription}
						analysisLanguage={aiGenerationState.analysisLanguage}
						projectType={aiGenerationState.projectType}
						aiContext={aiGenerationState.aiContext}
						aiScenarios={aiGenerationState.aiScenarios}
						onComplete={(projectId) => {
							setAiGenerationState(prev => ({ ...prev, visible: false }));
							const type = aiGenerationState.projectType || '';
							const params = new URLSearchParams({ onboarding: 'stakes' });
							if (type) params.set('type', type);
							history.push(`/projects/${projectId}/${t('pages.stakes.url')}?${params.toString()}`);
						}}
						onClose={() => {
							setAiGenerationState(prev => ({ ...prev, visible: false }));
							if (aiGenerationState.projectId) {
								history.push('/projects/' + aiGenerationState.projectId);
							}
						}}
					/>
				</React.Suspense>
			)}

			{AI_GENERATION_ENABLED && DisambiguationModal && disambiguationState.visible && disambiguationState.pendingPayload && (
				<React.Suspense fallback={null}>
					<DisambiguationModal
						visible={disambiguationState.visible}
						name={disambiguationState.pendingPayload.values?.name || ''}
						description={[
							disambiguationState.pendingPayload.values?.description,
							disambiguationState.pendingPayload.values?.descriptionLong
						].filter(Boolean).join('\n')}
						language={t('messages.ai_project_generation.language_code')}
						onClarified={async (enrichedDescription) => {
							const payload = disambiguationState.pendingPayload;
							setDisambiguationState({ visible: false, pendingPayload: null });
							if (!payload) return;
							// Now actually create the project with the (possibly
							// enriched) descriptionLong. If the user only picked
							// an interpretation without typing precisions,
							// enrichedDescription still includes the chosen label.
							const enrichedValues = {
								...payload.values,
								descriptionLong: enrichedDescription || payload.values.descriptionLong
							};
							await performCreateAndAiGen(enrichedValues, {
								aiGenerate: true,
								aiScenarios: payload.aiScenarios,
								aiContext: payload.aiContext,
								tempSessionId: payload.tempSessionId
							});
						}}
						onCancel={() => {
							// User cancelled clarification — no project gets
							// created, no DynamoDB junk left behind.
							setDisambiguationState({ visible: false, pendingPayload: null });
						}}
					/>
				</React.Suspense>
			)}
		</Layout.Content>

		</Layout >
	);
};
